import { Folder } from "../../redux/interfaces/folder.types.ts";

/**
 * A dónde lleva "Folio anterior" en la tarjeta de una renovación.
 *
 * - `direct`: el folio trae `previous_id`, así que se abre `/folders/{id}` sin
 *   ir a la red.
 * - `resolve`: sólo hay `previous_folio` (renovaciones manuales viejas, o un
 *   contrato declarado a mano que no está en el sistema). Hay que traducir el
 *   número a id con `GET /docvault/folders/{n}/folio` antes de navegar.
 */
export type PreviousFolioTarget =
  | { kind: "direct"; folderId: string; folioNumber: string | null }
  | { kind: "resolve"; folioNumber: string };

/**
 * MB-379 — decide el destino de "Folio anterior", o `null` si el folio no
 * declara origen. Es pura para poder probarla sin montar la tarjeta.
 */
export const previousFolioTarget = (
  folder: Folder,
): PreviousFolioTarget | null => {
  const renewal = folder.metadata?.renewal;
  const folioNumber = renewal?.previous_folio || null;
  const folderId = renewal?.previous_id || null;

  if (folderId) return { kind: "direct", folderId, folioNumber };
  if (folioNumber) return { kind: "resolve", folioNumber };
  return null;
};

/**
 * Ejecuta la navegación de "Folio anterior". Los efectos se inyectan para que se pueda
 * probar sin montar la tarjeta: lo que importa fijar es que el camino `direct` NO toca la
 * red ni abre la pestaña en blanco, y que el camino `resolve` conserva el flujo de antes.
 * Sin esa prueba, un refactor que llamara siempre a `resolve` pasaría todos los tests y
 * devolvería la petición extra en cada clic.
 */
export const openPreviousFolio = (
  target: PreviousFolioTarget,
  effects: {
    openInNewTab: (path: string) => void;
    resolveAndOpen: (folioNumber: string) => void | Promise<void>;
  },
): void => {
  if (target.kind === "direct") {
    effects.openInNewTab(`/folders/${target.folderId}`);
    return;
  }
  void effects.resolveAndOpen(target.folioNumber);
};
